import axios from 'axios';

// API configuration
// Production: VITE_API_URL MUST be set in Vercel env vars to point to Cloud Run backend.
// Development: falls back to localhost:8000.
// FAIL FAST: If VITE_API_URL is missing in production, throw immediately.
const isDev = import.meta.env.DEV;

let API_URL;
if (import.meta.env.VITE_API_URL) {
    API_URL = import.meta.env.VITE_API_URL;
} else if (isDev) {
    API_URL = 'http://localhost:8000';
} else {
    // Production without explicit VITE_API_URL:
    // Frontend is served from the same Cloud Run backend,
    // so use same-origin (empty string) for API calls.
    console.warn(
        '[Saige] VITE_API_URL not set at build time. ' +
        'Using same-origin for API calls (assumes frontend is served from backend).'
    );
    API_URL = '';
}

// Dev-only: verify backend connectivity on app load
if (isDev) {
    fetch(`${API_URL}/health`)
        .then(r => r.json())
        .then(data => console.log('[Saige] Backend health check:', data))
        .catch(err => console.error('[Saige] Backend unreachable:', err.message));
}

const api = axios.create({
    baseURL: API_URL,
    // No default Content-Type — let interceptor handle it so multipart uploads
    // can set their own boundary automatically.
});

// Add JWT token + smart Content-Type to all requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('saige_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Auto-detect: skip Content-Type for FormData (browser sets multipart boundary)
    if (!(config.data instanceof FormData) && !config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
    }
    return config;
});

export const checkUser = async (email, password) => {
    try {
        const response = await api.post('/check-user', { email, password });

        // Store JWT token if provided
        if (response.data.token) {
            localStorage.setItem('saige_token', response.data.token);
        }

        // Store role and tenant info
        if (response.data.role) {
            localStorage.setItem('saige_role', response.data.role);
        }
        if (response.data.district_id) {
            localStorage.setItem('saige_district_id', response.data.district_id);
        }
        if (response.data.campus_id) {
            localStorage.setItem('saige_campus_id', response.data.campus_id);
        }

        return response.data;
    } catch (error) {
        console.error("Check user failed", error);
        throw error; // Re-throw to handle in component
    }
};

export const isStagingReviewHost = () => {
    if (typeof window === 'undefined') return false;
    return window.location.hostname.startsWith('saige-backend-staging');
};

export const shouldUseStagingReviewSession = () => !isDev && isStagingReviewHost();

export const requestStagingReviewSession = async (role = 'teacher') => {
    const response = await api.post('/api/staging-review/session', { role });

    if (response.data.token) {
        localStorage.setItem('saige_token', response.data.token);
    }
    if (response.data.role) {
        localStorage.setItem('saige_role', response.data.role);
    }
    if (response.data.district_id) {
        localStorage.setItem('saige_district_id', response.data.district_id);
    } else {
        localStorage.removeItem('saige_district_id');
    }
    if (response.data.campus_id) {
        localStorage.setItem('saige_campus_id', response.data.campus_id);
    } else {
        localStorage.removeItem('saige_campus_id');
    }
    localStorage.setItem('saige_profile', JSON.stringify(response.data));

    return response.data;
};

export const logout = () => {
    localStorage.removeItem('saige_token');
    localStorage.removeItem('saige_profile');
    localStorage.removeItem('saige_role');
    localStorage.removeItem('saige_district_id');
    localStorage.removeItem('saige_campus_id');
};


// ============ OFFICIAL DOCUMENTS API ============

export const officialDocuments = {
    list: (params = {}) => api.get('/api/official-documents', { params }),
    getActive: () => api.get('/api/official-documents/active'),
    getById: (id) => api.get(`/api/official-documents/${id}`),

    upload: (formData) => api.post('/api/official-documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),

    activate: (id) => api.put(`/api/official-documents/${id}/activate`),
    retire: (id) => api.put(`/api/official-documents/${id}/retire`),
};


// ============ TEACHER CONTEXT API ============

export const teacherContext = {
    list: (params = {}) => api.get('/api/teacher-context', { params }),

    upload: (formData) => api.post('/api/teacher-context/upload', formData),

    update: (id, data) => api.put(`/api/teacher-context/${id}`, null, { params: data }),
    delete: (id) => api.delete(`/api/teacher-context/${id}`),

    // Admin: view campus teachers' context (read-only)
    listCampus: (campusId, params = {}) =>
        api.get(`/api/teacher-context/campus/${campusId}`, { params }),
};


// ============ PROFILE DOCUMENTS API ============
// General context files: pacing guides, syllabi, scope & sequence docs.
// Backed by /get-profile-docs, /upload-document, /delete-profile-doc in profile.py.
// Do NOT use /api/upload-curriculum for general context files.

export const profileDocuments = {
    list: () => api.get('/get-profile-docs'),
    upload: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload-document', formData);
    },
    delete: (docId) => api.post('/delete-profile-doc', { doc_id: docId }),
};


// ============ SETTINGS API ============

export const settings = {
    getAll: () => api.get('/api/settings'),
    get: (key) => api.get(`/api/settings/${key}`),
    update: (key, value, scope = 'campus') =>
        api.put(`/api/settings/${key}`, { value }, { params: { scope } }),
    getHistory: (key) => api.get(`/api/settings/history/${key}`),
};


// ============ ROLE HELPERS ============

export const getUserRole = () => localStorage.getItem('saige_role') || 'teacher';
export const isAdmin = () => {
    const role = getUserRole();
    return role === 'district_admin' || role === 'campus_admin';
};
export const isDistrictAdmin = () => getUserRole() === 'district_admin';
export const isCampusAdmin = () => getUserRole() === 'campus_admin';


// ============ AVATAR CREDITS API ============

export const avatarCredits = {
    getBalance: () => api.get('/api/avatar-credits/balance'),
    checkout: (bundleType) => api.post('/api/avatar-credits/checkout', { bundle_type: bundleType }),
    submitRequest: (script) => api.post('/api/avatar-credits/request', { script }),
    listRequests: () => api.get('/api/avatar-credits/requests'),
    getVideoUrl: (requestId) => api.get(`/api/avatar-credits/requests/${requestId}/video-url`),
};


// ============ BETA FEEDBACK API ============

export const betaFeedback = {
    weeklyStatus: () => api.get('/api/beta-feedback/weekly/status'),
    submitWeekly: (data) => api.post('/api/beta-feedback/weekly', data),
    submitUpdate: (data) => api.post('/api/beta-feedback/update', data),
    updateHistory: () => api.get('/api/beta-feedback/update/history'),
    // Categorized feedback (Sprint 4)
    submitCategorized: (data) => api.post('/api/beta-feedback/categorized', data),
    listCategorized: () => api.get('/api/beta-feedback/categorized'),
    patchCategorized: (id, data) => api.patch(`/api/beta-feedback/categorized/${id}`, data),
    // Sprint 4.5: Admin reply + teacher history
    replyCategorized: (id, data) => api.post(`/api/beta-feedback/categorized/${id}/reply`, data),
    myCategorized: () => api.get('/api/beta-feedback/categorized/my'),
    // Notifications
    myUnreadCount: () => api.get('/api/beta-feedback/categorized/my/unread-count'),
    markRead: (id) => api.post(`/api/beta-feedback/categorized/${id}/mark-read`),
    // Admin review — weekly & update feedback (P0.1)
    listWeekly: () => api.get('/api/beta-feedback/admin/weekly'),
    listUpdates: () => api.get('/api/beta-feedback/admin/updates'),
};


// ============ FEEDBACK ADMIN ACCESS API ============

export const feedbackAdmins = {
    list: () => api.get('/api/beta-feedback/admin/feedback-access'),
    add: (email, modules) => api.post('/api/beta-feedback/admin/feedback-access', { email, modules }),
    remove: (email) => api.delete(`/api/beta-feedback/admin/feedback-access/${encodeURIComponent(email)}`),
    myModules: () => api.get('/api/beta-feedback/admin/my-modules'),
};


// ============ INTERNAL ANALYTICS API ============

export const internalAnalytics = {
    getMetrics: () => api.get('/internal/analytics/metrics'),
};


// ============ DEV COMMAND CENTER API ============

export const devCenter = {
    getAccess: () => api.get('/api/admin/dev-center/access/me'),
    getToday: () => api.get('/api/admin/dev-center/today'),
    getDailySummaries: () => api.get('/api/admin/dev-center/daily-summary'),
    generateDailySummary: () => api.post('/api/admin/dev-center/daily-summary/generate'),
    getWorkItems: () => api.get('/api/admin/dev-center/work-items'),
    createWorkItem: (data) => api.post('/api/admin/dev-center/work-items', data),
    updateWorkItem: (id, data) => api.patch(`/api/admin/dev-center/work-items/${id}`, data),
    getSessions: (params) => api.get('/api/admin/dev-center/sessions', { params }),
    createSession: (data) => api.post('/api/admin/dev-center/sessions', data),
    getDeployments: (params) => api.get('/api/admin/dev-center/deployments', { params }),
    deployStaging: (data) => api.post('/api/admin/dev-center/deployments/staging', data),
    requestProdDeploy: (data) => api.post('/api/admin/dev-center/deployments/production-request', data),
    updateDeploymentStatus: (id, data) => api.patch(`/api/admin/dev-center/deployments/${id}/status`, data),
    createTestRun: (data) => api.post('/api/admin/dev-center/test-runs', data),
    submitTestResult: (id, data) => api.patch(`/api/admin/dev-center/test-runs/${id}`, data),
    submitFeedback: (data) => api.post('/api/admin/dev-center/feedback', data),
    getAccessList: () => api.get('/api/admin/dev-center/access'),
    addAccess: (data) => api.post('/api/admin/dev-center/access', data),
    revokeAccess: (id) => api.delete(`/api/admin/dev-center/access/${id}`),
};


// ============ ADMIN ANALYTICS V2 API ============

export const adminAnalytics = {
    getOverview: (params = {}) => api.get('/internal/analytics/overview', { params }),
    getFunnel: (params = {}) => api.get('/internal/analytics/funnel', { params }),
    getRetention: (params = {}) => api.get('/internal/analytics/retention', { params }),
    getFeatureUsage: (params = {}) => api.get('/internal/analytics/feature-usage', { params }),
    getHealth: (params = {}) => api.get('/internal/analytics/health', { params }),
    getFeedbackThemes: (params = {}) => api.get('/internal/analytics/feedback/themes', { params }),
    getIssueClusters: (params = {}) => api.get('/internal/analytics/issues/clusters', { params }),
    getSuggestionClusters: (params = {}) => api.get('/internal/analytics/suggestions/clusters', { params }),
    getInsightsLatest: (params = {}) => api.get('/internal/analytics/insights/latest', { params }),
    refreshInsights: (params = {}) => api.post('/internal/analytics/insights/refresh', null, { params }),
    // V3 — event explorer, metric detail, usage time
    getEvents: (params = {}) => api.get('/internal/analytics/events', { params }),
    getMetricDetail: (params = {}) => api.get('/internal/analytics/metric-detail', { params }),
    getUsageTime: (params = {}) => api.get('/internal/analytics/usage-time', { params }),
    // Phase 4A — monetization analytics (backend aggregation)
    getMonetization: (params = {}) => api.get('/internal/analytics/monetization', { params }),
    // Phase 4B — decision layer
    getCampusIntelligenceAnalytics: (params = {}) => api.get('/internal/analytics/campus-intelligence', { params }),
    getWorkflowAnalytics: (params = {}) => api.get('/internal/analytics/workflow', { params }),
    getDecisionSummary: (params = {}) => api.get('/internal/analytics/decision-summary', { params }),
    getGenerationTime: (params = {}) => api.get('/internal/analytics/generation-time', { params }),
    // Phase 5 — Live Ops + Teacher Value surface
    getWatchItems: (params = {}) => api.get('/internal/analytics/watch-items', { params }),
    getLiveActivity: () => api.get('/internal/analytics/live-activity'),
    getOpsSummary: () => api.get('/internal/analytics/ops-summary'),
    getLessonReliability: (params = {}) => api.get('/internal/analytics/lesson-reliability', { params }),
    getSessionQuality: (params = {}) => api.get('/internal/analytics/session-quality', { params }),
    getLessonJourney: (params = {}) => api.get('/internal/analytics/lesson-journey', { params }),
    getUserTimeline: (userUuid, params = {}) => api.get(`/internal/analytics/user-timeline/${userUuid}`, { params }),
    getWorkspaceSummary: () => api.get('/internal/analytics/metrics/workspace_summary'),
};


// ============ VOICEOVER API ============

export const voiceover = {
    cloneVoice: (formData) => api.post('/api/voices/clone', formData),
    getMyVoice: (teacherId) => api.get('/api/voices/me', { params: { teacher_id: teacherId } }),
    deleteMyVoice: (teacherId) => api.delete('/api/voices/me', { params: { teacher_id: teacherId } }),
    previewVoice: (data) => api.post('/api/voices/preview', data, { responseType: 'blob' }),
    updateVoicePreset: (data) => api.post('/api/voices/update-preset', data),
    getSourceSample: (teacherId) => api.get('/api/voices/source-sample', { params: { teacher_id: teacherId } }),
    getVoicePresets: (teacherId) => api.get('/api/voices/presets', { params: { teacher_id: teacherId } }),
    generateScript: (data) => api.post('/api/voiceover/script', data),
    generateAudio: (data) => api.post('/api/voiceover/generate', data),
    getVoiceover: (lessonDocId, teacherId) =>
        api.get(`/api/voiceover/${lessonDocId}`, { params: { teacher_id: teacherId } }),
};


// ============ LESSON CHAT API (Phase 3.1) ============

export const lessonChat = {
    send: (data) => api.post('/api/production/lesson-chat', data),
};


// ============ GRADING STUDIO API ============

export const gradingStudio = {
    /**
     * Evaluate student submissions via FERPA-safe grading pipeline.
     * @param {Object} data
     * @param {string} data.teacher_id
     * @param {Array<{code_name: string, work: string}>} data.submissions
     * @param {string} [data.rubric_text]
     * @param {string[]} [data.standards]
     * @param {number} [data.max_points]
     */
    evaluate: (data) => api.post('/api/production/evaluate-assignment', data),
};


// ============ FEATURE FLAGS (Phase 3A) ============

export const featureFlags = {
    get: () => api.get('/api/feature-flags'),
};


// ============ CAMPUS INTELLIGENCE API ============

export const campusIntelligence = {
    // Documents
    extractText: (id) => api.post(`/api/campus-intelligence/documents/${id}/extract-text`),
    getText: (id) => api.get(`/api/campus-intelligence/documents/${id}/text`),
    
    // Structured Extraction
    triggerExtraction: (id) => api.post(`/api/campus-intelligence/extract/${id}`),
    
    // Contacts
    getContacts: (params = {}) => api.get('/api/campus-intelligence/contacts', { params }),
    upsertContact: (data) => api.post('/api/campus-intelligence/contacts', data),
    approveContact: (id) => api.put(`/api/campus-intelligence/contacts/${id}/approve`),
    deleteContact: (id) => api.delete(`/api/campus-intelligence/contacts/${id}`),

    // Events
    getEvents: (params = {}) => api.get('/api/campus-intelligence/events', { params }),
    upsertEvent: (data) => api.post('/api/campus-intelligence/events', data),
    approveEvent: (id) => api.put(`/api/campus-intelligence/events/${id}/approve`),
    deleteEvent: (id) => api.delete(`/api/campus-intelligence/events/${id}`),

    // Schedules
    getSchedules: (params = {}) => api.get('/api/campus-intelligence/schedules', { params }),
    upsertSchedule: (data) => api.post('/api/campus-intelligence/schedules', data),
    approveSchedule: (id) => api.put(`/api/campus-intelligence/schedules/${id}/approve`),
    promoteSchedule: (id, scope) => api.post(`/api/campus-intelligence/schedules/${id}/promote`, { scope }),
    deleteSchedule: (id) => api.delete(`/api/campus-intelligence/schedules/${id}`),

    // Policies
    getPolicies: (params = {}) => api.get('/api/campus-intelligence/policies', { params }),
    upsertPolicy: (data) => api.post('/api/campus-intelligence/policies', data),
    approvePolicy: (id) => api.put(`/api/campus-intelligence/policies/${id}/approve`),
    deletePolicy: (id) => api.delete(`/api/campus-intelligence/policies/${id}`),
    
    // Context Preview
    getContextPreview: (params = {}) => api.get('/api/campus-intelligence/context', { params })
};


// ============ PHASE 3 OPERATING STATE API ============

export const operatingState = {
    getMine: () => api.get('/api/operating-state/me'),
    getAdminSummary: (params = {}) => api.get('/api/operating-state/admin/summary', { params }),
    getConfigPack: () => api.get('/api/operating-state/config-pack'),
    saveConfigPack: (data) => api.post('/api/operating-state/config-pack', data),
    createStudentSignal: (data) => api.post('/api/operating-state/student-signals', data),
    createCommunicationDraft: (data) => api.post('/api/operating-state/communication-draft', data),
};


/**
 * Load feature flags + experiment variants from backend and store in localStorage.
 * Call once on app mount. Non-blocking — fails silently.
 * Phase 4A: Also stores experiment variants for monetization A/B testing.
 */
export async function loadFeatureFlags() {
    try {
        const res = await featureFlags.get();
        if (res.data?.flags) {
            localStorage.setItem('saige_feature_flags', JSON.stringify(res.data.flags));
        }
        if (res.data?.variants) {
            localStorage.setItem('saige_experiment_variants', JSON.stringify(res.data.variants));
        }
    } catch {
        // Non-blocking — flags will use defaults
    }
}

/**
 * Get the assigned experiment variant for a given experiment key.
 * Returns the variant letter (e.g. 'A', 'B') or default.
 *
 * @param {string} key — e.g. 'nudge_post_lesson_variant'
 * @param {string} defaultVariant — fallback (default 'A')
 * @returns {string}
 */
export function getExperimentVariant(key, defaultVariant = 'A') {
    try {
        const variants = JSON.parse(localStorage.getItem('saige_experiment_variants') || '{}');
        return variants[key] || defaultVariant;
    } catch {
        return defaultVariant;
    }
}


export default api;
